import { useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle, Maximize2, Info } from "lucide-react";
import { PdfComparison } from "../utils/compare-pdfs";
import { Button } from "../../../../components/ui/button";
import { PdfPageViewerModal } from "./pdf-page-viewer-modal.component";

interface PdfComparisonCardProps {
	comparison: PdfComparison;
	allComparisons: PdfComparison[];
	comparisonIndex: number;
	reviewState: Map<string, boolean | undefined>;
	onUpdateReview: (comparison: PdfComparison, status: boolean | undefined) => void;
}

export const PdfComparisonCard: React.FC<PdfComparisonCardProps> = ({
	comparison,
	allComparisons,
	comparisonIndex,
	reviewState,
	onUpdateReview,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [viewerModalOpen, setViewerModalOpen] = useState(false);
	const [selectedPageIndex, setSelectedPageIndex] = useState(0);

	const totalDiffPercentage = comparison.pages.reduce(
		(sum, page) => sum + page.diffPercentage,
		0
	) / comparison.pages.length;

	const handleOpenViewer = (pageIndex: number) => {
		setSelectedPageIndex(pageIndex);
		setViewerModalOpen(true);
	};

	return (
		<div className="bg-white border-2 border-red-500 rounded-lg overflow-hidden">
			<div
				className="p-4 bg-red-100 cursor-pointer hover:bg-red-200 transition-colors"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<AlertTriangle className="w-5 h-5 text-red-900" />
						<div>
							<h3 className="font-semibold text-gray-900">
								{comparison.orgId}
							</h3>
							<p className="text-sm text-gray-700">
								{comparison.clientName}
							</p>
							<p className="text-xs text-gray-600">
								{comparison.pdfType} • {comparison.maxPages} Seite{comparison.maxPages !== 1 ? 'n' : ''}
							</p>
							{comparison.settingsPart && (
								<div className="flex flex-wrap gap-1 mt-1">
									{comparison.settingsPart.split('_').map((setting, idx) => (
										<span
											key={idx}
											className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-800"
										>
											{setting}
										</span>
									))}
								</div>
							)}
						</div>
					</div>
					<div className="flex items-center gap-4">
						<div className="text-right">
							<div className="text-lg font-bold text-red-950">
								{totalDiffPercentage.toFixed(2)}%
							</div>
							<div className="text-xs text-red-700">
								unterschiedlich
							</div>
						</div>
						<Button
							variant="ghost"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								setIsExpanded(!isExpanded);
							}}
							aria-label={isExpanded ? "Zuklappen" : "Aufklappen"}
						>
							{isExpanded ? (
								<ChevronUp className="w-5 h-5" />
							) : (
								<ChevronDown className="w-5 h-5" />
							)}
						</Button>
					</div>
				</div>
			</div>

			{isExpanded && (
				<div className="p-6 space-y-6 bg-gray-50">
					{comparison.pages.map((page, idx) => (
						<div
							key={page.pageNumber}
							className={`border rounded-lg overflow-hidden ${
								page.hasDifference
									? 'border-2 border-red-500 bg-white'
									: 'border-2 border-green-600 bg-green-100'
							}`}
						>
							<div className={`p-3 ${
								page.hasDifference ? 'bg-red-100' : 'bg-green-100'
							}`}>
								<div className="flex items-center justify-between">
									<h4 className="font-medium text-gray-900">
										Seite {page.pageNumber}
									</h4>
									<div className="flex items-center gap-3">
										{page.hasDifference && (
											<div className="text-sm">
												<span className="font-semibold text-red-950">
													{page.diffPercentage.toFixed(2)}%
												</span>
												<span className="text-red-900 ml-1">
													({page.diffPixels.toLocaleString()} von {page.totalPixels.toLocaleString()} Pixel)
												</span>
											</div>
										)}
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleOpenViewer(idx)}
											aria-label="Vergrößern"
											data-testid={`maximize-page-${page.pageNumber}`}
										>
											<Maximize2 className="w-4 h-4" />
										</Button>
									</div>
								</div>
							</div>

							{page.hasDifference && (
								<div className="p-4">
									<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
										<div>
											<h5 className="text-sm font-medium text-gray-700 mb-2">
												ZIP 1 (Referenz)
											</h5>
											{!page.hasPage1 && (
												<div className="bg-orange-100 border border-orange-600 rounded p-2 mb-2 flex items-center gap-2">
													<Info className="w-3 h-3 text-orange-900 flex-shrink-0" />
													<span className="text-xs font-semibold text-orange-900">
														Nicht verfügbar
													</span>
												</div>
											)}
											<div
												className="border border-gray-300 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
												onClick={() => handleOpenViewer(idx)}
											>
												<img
													src={page.page1Canvas.toDataURL()}
													alt={`ZIP 1 - Seite ${page.pageNumber}`}
													className={`w-full h-auto ${!page.hasPage1 ? 'opacity-30' : ''}`}
												/>
											</div>
										</div>

										<div>
											<h5 className="text-sm font-medium text-gray-700 mb-2">
												ZIP 2 (Vergleich)
											</h5>
											{!page.hasPage2 && (
												<div className="bg-orange-100 border border-orange-600 rounded p-2 mb-2 flex items-center gap-2">
													<Info className="w-3 h-3 text-orange-900 flex-shrink-0" />
													<span className="text-xs font-semibold text-orange-900">
														Nicht verfügbar
													</span>
												</div>
											)}
											<div
												className="border border-gray-300 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
												onClick={() => handleOpenViewer(idx)}
											>
												<img
													src={page.page2Canvas.toDataURL()}
													alt={`ZIP 2 - Seite ${page.pageNumber}`}
													className={`w-full h-auto ${!page.hasPage2 ? 'opacity-30' : ''}`}
												/>
											</div>
										</div>

										<div>
											<h5 className="text-sm font-medium text-red-900 mb-2">
												Unterschiede
											</h5>
											<div
												className="border-2 border-red-500 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
												onClick={() => handleOpenViewer(idx)}
											>
												<img
													src={page.diffCanvas.toDataURL()}
													alt={`Unterschiede - Seite ${page.pageNumber}`}
													className="w-full h-auto"
												/>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<PdfPageViewerModal
				comparisons={allComparisons}
				initialComparisonIndex={comparisonIndex}
				initialPageIndex={selectedPageIndex}
				isOpen={viewerModalOpen}
				onClose={() => setViewerModalOpen(false)}
				reviewState={reviewState}
				onUpdateReview={onUpdateReview}
			/>
		</div>
	);
};
